import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
  maxWidth?: string;
}

interface ListPageSkeletonProps extends SkeletonProps {
  rows?: number;
  showStats?: boolean;
  showPills?: boolean;
  showFilters?: boolean;
}

// Mirrors AllOpportunitiesView: max-w-7xl container, header+actions,
// stats row, type pills, filter/search row, list rows.
export const ListPageSkeleton = ({
  className,
  maxWidth = 'max-w-7xl',
  rows = 8,
  showStats = true,
  showPills = true,
  showFilters = true,
}: ListPageSkeletonProps) => {
  return (
    <div className={cn('flex min-h-full w-full flex-col', className)}>
      <div className={cn('mx-auto p-6 space-y-6 w-full flex-1', maxWidth)}>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-80 max-w-full" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-44" />
            <Skeleton className="h-9 w-40" />
          </div>
        </div>

        {showStats && (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        )}

        {showPills && (
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-4 w-10 mr-1" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-28 rounded-full" />
            ))}
          </div>
        )}

        {showFilters && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-56" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-8 w-40" />
            </div>
          </div>
        )}

        <div className="space-y-2">
          {Array.from({ length: rows }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
};

interface DetailPageSkeletonProps extends SkeletonProps {
  sections?: number;
  showBreadcrumbs?: boolean;
}

// Mirrors OpportunityDetailPage: max-w-7xl container, breadcrumbs,
// header (title + badges + actions), multi-section body.
export const DetailPageSkeleton = ({
  className,
  maxWidth = 'max-w-7xl',
  sections = 3,
  showBreadcrumbs = true,
}: DetailPageSkeletonProps) => {
  return (
    <div className={cn('flex min-h-full w-full flex-col', className)}>
      <div className={cn('mx-auto p-6 space-y-6 w-full flex-1', maxWidth)}>
        {showBreadcrumbs && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-3.5 w-24" />
            <span className="text-muted-foreground/40">/</span>
            <Skeleton className="h-3.5 w-40" />
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3 flex-1">
            <Skeleton className="h-8 w-2/3 max-w-xl" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-36" />
          </div>
        </div>

        <div className="space-y-6">
          {Array.from({ length: sections }).map((_, i) => (
            <div key={i} className="space-y-3 rounded-lg border border-border/60 p-5">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Default fallback used for Suspense — list-shaped, since most pages are lists.
export const PageSkeleton = ListPageSkeleton;
